import { CSSProperties, FC, ReactNode, useEffect, useState } from "react";
import { GlobalToastContainer } from "@/components/toast/global-container";
import { AtlasRouter } from "@/app/router/router";
import { AtlasStyleWrapper } from "@/domains/styling/wrapper";
import { AtlasAuthProvider } from "@/contexts/atlas-auth";
import { AtlasAssetTokenProvider } from "@/contexts/atlas-asset-token";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Theme, ThemeProps } from "@radix-ui/themes";
import { AtlasGlobalProvider } from "@/domains/styling/global-provider";
import { isIOSDevice } from "@/utils/device-detection";
import { ThemePreferenceProvider, useThemePreference } from "@/contexts/theme-preference";

const atlasTheme: ThemeProps = {
  accentColor: "violet",
  grayColor: "gray",
  scaling: "90%",
  radius: "small",
};

export const AtlasCore: FC = () => {
  const client = new QueryClient();
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check for iOS on mount only - iOS detection doesn't change during session
    setIsIOS(isIOSDevice());
  }, []);

  const theme = { ...atlasTheme, scaling: isIOS ? "110%" : atlasTheme.scaling };

  return (
    <AtlasAuthProvider>
      <AtlasAssetTokenProvider>
        <QueryClientProvider client={client}>
          <ThemePreferenceProvider>
            <AtlasThemeAppearance themeProps={theme}>
              <AtlasStyleWrapper>
                <AtlasGlobalProvider>
                  <AtlasRouter />
                  <GlobalToastContainer />
                  <MigrationModal />
                </AtlasGlobalProvider>
              </AtlasStyleWrapper>
            </AtlasThemeAppearance>
          </ThemePreferenceProvider>
        </QueryClientProvider>
      </AtlasAssetTokenProvider>
    </AtlasAuthProvider>
  );
};

type AtlasThemeAppearanceProps = {
  themeProps: ThemeProps;
  children: ReactNode;
};

const migrationOverlay: CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 99999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(0, 0, 0, 0.7)",
  backdropFilter: "blur(4px)",
};

const migrationBox: CSSProperties = {
  position: "relative",
  backgroundColor: "#fff",
  borderRadius: 16,
  padding: "60px 48px",
  maxWidth: 520,
  width: "90%",
  textAlign: "center",
  boxShadow: "0 24px 80px rgba(0,0,0,0.3)",
};

const migrationClose: CSSProperties = {
  position: "absolute",
  top: 12,
  right: 14,
  background: "none",
  border: "none",
  fontSize: 14,
  color: "#999",
  cursor: "pointer",
  padding: 4,
  lineHeight: 1,
};

const MigrationModal: FC = () => {
  const [open, setOpen] = useState(true);
  if (!open) return null;

  return (
    <div style={migrationOverlay} onClick={() => setOpen(false)}>
      <div style={migrationBox} onClick={(e) => e.stopPropagation()}>
        <button style={migrationClose} onClick={() => setOpen(false)}>
          ✕
        </button>
        <h1 style={{ margin: "0 0 16px", fontSize: 28, color: "#111" }}>
          We&apos;ve moved!
        </h1>
        <p style={{ margin: "0 0 28px", fontSize: 16, color: "#555", lineHeight: 1.5 }}>
          The new Atlas is now at:
        </p>
        <a
          href="https://atlas-membot.mem.ai"
          style={{
            display: "inline-block",
            fontSize: 22,
            fontWeight: 600,
            color: "#6e56cf",
            textDecoration: "none",
            padding: "14px 32px",
            borderRadius: 10,
            backgroundColor: "#f0ecfc",
          }}
        >
          atlas-membot.mem.ai
        </a>
        <p style={{ marginTop: 28, fontSize: 13, color: "#999" }}>
          Please update your bookmarks.
        </p>
      </div>
    </div>
  );
};

const AtlasThemeAppearance = ({ themeProps, children }: AtlasThemeAppearanceProps) => {
  const { resolvedTheme } = useThemePreference();
  const themeAppearance: ThemeProps["appearance"] =
    resolvedTheme === "dark" ? "light" : resolvedTheme;

  return (
    <Theme {...themeProps} appearance={themeAppearance}>
      {children}
    </Theme>
  );
};
